import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area } from "recharts";
import { Plus, Trash2, Zap, Sun, Leaf, Battery, TrendingUp, Home as HomeIcon, Lock, Sparkles, Power, PowerOff, AlertTriangle, Wrench, Activity, Thermometer, Gauge, Radio, Download, Lightbulb, Cloud, CloudRain, CloudSun, ArrowUp, ArrowDown, Euro, GitCompare, Mail, BellOff, LineChart as LineChartIcon } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

export default function Dashboard() {
  const { t, user } = useApp();
  const [plants, setPlants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [prod, setProd] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);

  const loadPlants = async () => {
    if (!user?.is_pro) { setLoading(false); return; }
    try {
      const [{ data: p }, { data: h }] = await Promise.all([
        api.get("/plants"),
        api.get("/calculator/history"),
      ]);
      setPlants(p);
      setHistory(h);
      if (p.length > 0 && !selected) setSelected(p[0].id);
    } catch { toast.error("Load error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadPlants(); }, [user?.is_pro]);

  const [live, setLive] = useState(null);
  const [events, setEvents] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [devices, setDevices] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [energyPrices, setEnergyPrices] = useState(null);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    if (!selected) { setProd(null); setLive(null); setEvents([]); setForecast(null); setAnomalies([]); setDevices([]); setSuggestions(null); setInsights(null); return; }
    api.get(`/plants/${selected}/production`).then(({ data }) => setProd(data)).catch(() => {});
    api.get(`/plants/${selected}/events`).then(({ data }) => setEvents(data)).catch(() => {});
    api.get(`/plants/${selected}/forecast`).then(({ data }) => setForecast(data)).catch(() => {});
    api.get(`/plants/${selected}/anomalies`).then(({ data }) => setAnomalies(data.anomalies || [])).catch(() => {});
    api.get(`/plants/${selected}/devices`).then(({ data }) => setDevices(data)).catch(() => {});
    api.get(`/plants/${selected}/ai-suggestions`).then(({ data }) => setSuggestions(data.suggestions || [])).catch(() => {});
    api.get(`/plants/${selected}/insights`).then(({ data }) => setInsights(data)).catch(() => {});
    api.get(`/energy/prices?days=7`).then(({ data }) => setEnergyPrices(data)).catch(() => {});
    // Auto anomaly check (silent — server dedupes 24h per plant)
    api.post(`/plants/${selected}/check-anomaly`).then(({ data }) => {
      if (data?.status === "anomaly" && data.email_sent) {
        toast.warning(`Anomalia rilevata: -${data.drop_pct}%. Ti abbiamo mandato un'email.`, { duration: 8000 });
      }
    }).catch(() => {});
    // Live poll every 5s
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      api.get(`/plants/${selected}/live`).then(({ data }) => !cancelled && setLive(data)).catch(() => {});
    };
    tick();
    const interval = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [selected]);

  const downloadReport = async () => {
    try {
      const token = localStorage.getItem("solair_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plants/${selected}/report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `report-${selected.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF error"); }
  };

  const togglePower = async () => {
    if (!selected || !live) return;
    const next = live.status === "online" ? "offline" : "online";
    try {
      await api.patch(`/plants/${selected}/status`, { status: next });
      const { data } = await api.get(`/plants/${selected}/live`);
      setLive(data);
      const { data: ev } = await api.get(`/plants/${selected}/events`);
      setEvents(ev);
      toast.success(next === "online" ? "Impianto avviato" : "Impianto spento");
    } catch { toast.error("Errore comando"); }
  };

  const deletePlant = async (id) => {
    if (!window.confirm("Delete plant?")) return;
    await api.delete(`/plants/${id}`);
    setSelected(null);
    loadPlants();
  };

  const deleteCalc = async (id) => {
    await api.delete(`/calculator/${id}`);
    loadPlants();
  };

  if (loading) return <div className="p-12 text-center text-[#6E7A71]">Loading...</div>;

  if (!user?.is_pro) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] grain-bg pb-16">
        <div className="max-w-3xl mx-auto px-6 pt-16">
          <div className="card-surface p-12 text-center" data-testid="dashboard-locked">
            <div className="w-16 h-16 rounded-full bg-[#EAB308] flex items-center justify-center mx-auto mb-6">
              <Lock size={26} className="text-[#17301C]" />
            </div>
            <div className="label-eco mb-3">Funzione Pro</div>
            <h1 className="text-4xl font-black text-[#17301C] tracking-tight mb-4">
              La Dashboard è riservata al piano <span className="text-[#EAB308]">Pro</span>
            </h1>
            <p className="text-[#3A4A3E] mb-8">
              Monitora produzione oraria, mensile e annuale dei tuoi impianti, con KPI e grafici avanzati. Sblocca tutto a €2,50/mese.
            </p>
            <Link to="/pricing" className="btn-primary" data-testid="upgrade-from-dashboard">
              <Sparkles size={16} /> Passa a Pro
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain-bg pb-16">
      <div className="max-w-7xl mx-auto px-6 pt-12">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="label-eco mb-3">Dashboard</div>
            <h1 className="text-5xl font-black tracking-tight text-[#17301C]">{t.dashboard.title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/compare" className="btn-dash" data-testid="link-compare"><GitCompare size={14} /> Confronta</Link>
            <EmailPrefsToggle />
            {selected && <AnomalyCheckButton plantId={selected} />}
            <AddPlantDialog open={addOpen} setOpen={setAddOpen} onCreated={loadPlants} />
          </div>
        </div>

        <ConnectInverterDialog
          plantId={selected}
          plantName={plants.find((p) => p.id === selected)?.name || ""}
          open={connectOpen}
          setOpen={setConnectOpen}
        />

        {plants.length === 0 ? (
          <div className="card-surface p-16 text-center">
            <HomeIcon size={40} className="text-[#8BA892] mx-auto mb-4" />
            <p className="text-[#3A4A3E]">{t.dashboard.no_plants}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Sidebar */}
            <div className="md:col-span-3 space-y-3">
              {plants.map((p) => (
                <button key={p.id} data-testid={`plant-tab-${p.id}`} onClick={() => setSelected(p.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-colors ${
                    selected === p.id ? "bg-[#17301C] text-white border-[#17301C]" : "bg-white border-[#DDE0D8] hover:bg-[#EAE7E0]"
                  }`}>
                  <div className="text-xs uppercase tracking-widest opacity-70">{p.location}</div>
                  <div className="font-bold text-lg mt-1">{p.name}</div>
                  <div className="text-sm font-mono-nums opacity-80 mt-1">{p.kwp} kWp · {p.panels} panels</div>
                </button>
              ))}
            </div>

            {/* Main */}
            <div className="md:col-span-9 space-y-6">
              {/* INSIGHTS PANEL - AI daily + Economic + Comparison */}
              {insights && <InsightsPanel data={insights} />}

              {/* LIVE monitoring card */}
              {live && (
                <div className="card-surface p-6" data-testid="live-panel">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className={`relative w-3 h-3 rounded-full ${
                        live.status === "online" ? "bg-[#166534]" :
                        live.status === "fault" ? "bg-[#991B1B]" :
                        live.status === "maintenance" ? "bg-[#EAB308]" : "bg-[#6E7A71]"
                      }`}>
                        {live.status === "online" && <span className="absolute inset-0 rounded-full bg-[#166534] animate-ping opacity-60" />}
                      </div>
                      <div>
                        <div className="label-eco">Monitoraggio Live · {live.inverter_brand}</div>
                        <div className="text-xs text-[#6E7A71] font-mono-nums">
                          {new Date(live.timestamp).toLocaleTimeString("it-IT")} · Status: <span className="font-bold text-[#17301C] uppercase">{live.status}</span>
                        </div>
                      </div>
                    </div>
                    <button onClick={togglePower} data-testid="plant-power-toggle"
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                        live.status === "online"
                          ? "bg-[#991B1B] text-white hover:bg-[#7f1414]"
                          : "bg-[#166534] text-white hover:bg-[#124d27]"
                      }`}>
                      {live.status === "online" ? <><PowerOff size={14} /> Spegni</> : <><Power size={14} /> Avvia</>}
                    </button>
                    <button onClick={() => setConnectOpen(true)} data-testid="connect-inverter-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-[#EAB308] text-[#17301C] hover:bg-[#f5bc1b] transition-colors">
                      <Radio size={14} /> Collega inverter
                    </button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <LiveKpi icon={Activity} label="Potenza istantanea" value={live.current_power_kw} unit="kW" hint={`${live.current_ratio_pct}% del picco`} accent />
                    <LiveKpi icon={HomeIcon} label="Consumo casa/azienda" value={live.consumption_kw} unit="kW" hint={`${live.self_consumption_pct}% autoconsumo`} />
                    <LiveKpi icon={ArrowUp} label="Immissione in rete" value={live.grid_export_kw} unit="kW" hint={live.grid_export_kw > 0 ? "In vendita" : "-"} />
                    <LiveKpi icon={ArrowDown} label="Prelievo dalla rete" value={live.grid_import_kw} unit="kW" hint={live.grid_import_kw > 0 ? "In acquisto" : "-"} />
                    <LiveKpi icon={Gauge} label="Frequenza rete" value={live.grid_frequency_hz} unit="Hz" hint="50 Hz nominale" />
                    <LiveKpi icon={Radio} label="Tensione" value={live.grid_voltage_v} unit="V" hint="230 V nominale" />
                    <LiveKpi icon={Thermometer} label="Temp. inverter" value={live.inverter_temp_c} unit="°C" hint={live.inverter_temp_c > 60 ? "Alta" : "Normale"} />
                    <LiveKpi icon={Euro} label="Risparmio ora" value={`${((live.self_consumption_kw * 0.28) + (live.grid_export_kw * 0.09)).toFixed(2)}`} unit="€/h" />
                  </div>
                  {live.battery_soc_pct != null && (
                    <div className="mt-4 p-3 rounded-lg bg-[#EAE7E0] flex items-center gap-3">
                      <Battery size={18} className="text-[#17301C]" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="font-bold text-[#17301C]">Stato batteria</span>
                          <span className="font-mono-nums font-bold text-[#17301C]">{live.battery_soc_pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-white overflow-hidden">
                          <div className="h-full bg-[#EAB308]" style={{ width: `${live.battery_soc_pct}%` }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {prod && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KpiCard icon={Zap} label={t.dashboard.current_power} value={prod.kpi.current_power_kw} unit="kW" accent />
                    <KpiCard icon={Sun} label={t.dashboard.today} value={prod.kpi.today_kwh} unit="kWh" />
                    <KpiCard icon={TrendingUp} label={t.dashboard.month} value={prod.kpi.month_kwh.toLocaleString()} unit="kWh" />
                    <KpiCard icon={Leaf} label={`${t.dashboard.year} CO₂`} value={prod.kpi.co2_saved_tonnes} unit="t" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <ChartCard title={t.dashboard.today_hourly}>
                      <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={prod.today_hourly}>
                          <defs>
                            <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#EAB308" stopOpacity={0.6} />
                              <stop offset="100%" stopColor="#EAB308" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#DDE0D8" />
                          <XAxis dataKey="hour" stroke="#6E7A71" fontSize={11} />
                          <YAxis stroke="#6E7A71" fontSize={11} />
                          <Tooltip contentStyle={{ background: "#fff", border: "1px solid #DDE0D8", borderRadius: 8 }} />
                          <Area dataKey="kwh" stroke="#EAB308" strokeWidth={2} fill="url(#g1)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </ChartCard>
                    <ChartCard title={t.dashboard.last_30_days}>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={prod.last_30_days}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#DDE0D8" />
                          <XAxis dataKey="day" stroke="#6E7A71" fontSize={11} />
                          <YAxis stroke="#6E7A71" fontSize={11} />
                          <Tooltip contentStyle={{ background: "#fff", border: "1px solid #DDE0D8", borderRadius: 8 }} />
                          <Bar dataKey="kwh" fill="#17301C" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartCard>
                  </div>

                  <ChartCard title={t.dashboard.monthly_year}>
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={prod.monthly_year}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#DDE0D8" />
                        <XAxis dataKey="month" stroke="#6E7A71" fontSize={11} />
                        <YAxis stroke="#6E7A71" fontSize={11} />
                        <Tooltip contentStyle={{ background: "#fff", border: "1px solid #DDE0D8", borderRadius: 8 }} />
                        <Line dataKey="kwh" stroke="#EAB308" strokeWidth={3} dot={{ fill: "#17301C", r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  {/* Events log */}
                  <div className="card-surface p-6" data-testid="events-log">
                    <div className="flex items-center justify-between mb-4">
                      <div className="label-eco">Registro eventi</div>
                      <div className="text-xs text-[#6E7A71] font-mono-nums">{events.length}</div>
                    </div>
                    {events.length === 0 ? (
                      <p className="text-sm text-[#6E7A71]">Nessun evento registrato.</p>
                    ) : (
                      <div className="space-y-2 max-h-72 overflow-y-auto">
                        {events.map((e) => {
                          const IconMap = { startup: Power, online: Power, offline: PowerOff, fault: AlertTriangle, maintenance: Wrench, shutdown: PowerOff, note: Activity };
                          const ColorMap = { startup: "text-[#166534]", online: "text-[#166534]", offline: "text-[#6E7A71]", shutdown: "text-[#6E7A71]", fault: "text-[#991B1B]", maintenance: "text-[#EAB308]", note: "text-[#17301C]" };
                          const Ic = IconMap[e.type] || Activity;
                          return (
                            <div key={e.id} className="flex items-start gap-3 py-2 border-b border-[#DDE0D8] last:border-0" data-testid={`event-${e.id}`}>
                              <Ic size={16} className={`mt-0.5 flex-shrink-0 ${ColorMap[e.type] || "text-[#17301C]"}`} />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm text-[#17301C]">{e.message}</div>
                                <div className="text-[10px] uppercase tracking-widest text-[#6E7A71] font-mono-nums">
                                  {new Date(e.created_at).toLocaleString("it-IT")} · {e.type}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-center">
                    <button data-testid="download-report" onClick={downloadReport}
                      className="text-sm text-[#17301C] hover:underline flex items-center gap-1 font-semibold"><Download size={14} /> Scarica report PDF</button>
                    <button data-testid={`delete-plant-${selected}`} onClick={() => deletePlant(selected)}
                      className="text-sm text-[#991B1B] hover:underline flex items-center gap-1"><Trash2 size={14} /> Delete plant</button>
                  </div>

                  {/* FORECAST 7 giorni */}
                  {forecast && (
                    <div className="card-surface p-6" data-testid="forecast-panel">
                      <div className="flex items-center justify-between mb-4">
                        <div className="label-eco flex items-center gap-2"><CloudSun size={14} /> Previsioni produzione 7 giorni</div>
                        <div className="text-sm font-mono-nums font-bold text-[#17301C]">{forecast.total_week_kwh} kWh totali</div>
                      </div>
                      <div className="grid grid-cols-7 gap-2">
                        {forecast.forecast.map((f) => {
                          const WI = { sunny: Sun, partly: CloudSun, cloudy: Cloud, rain: CloudRain };
                          const IC = WI[f.weather] || Sun;
                          const color = { sunny: "text-[#EAB308]", partly: "text-[#EAB308]", cloudy: "text-[#6E7A71]", rain: "text-[#3A6EA5]" }[f.weather];
                          return (
                            <div key={f.date} className="text-center p-3 rounded-lg bg-[#F9F8F6] border border-[#DDE0D8]">
                              <div className="text-[10px] uppercase font-bold text-[#6E7A71]">{new Date(f.date).toLocaleDateString("it-IT", { weekday: "short" })}</div>
                              <IC size={26} className={`${color} mx-auto my-2`} />
                              <div className="text-sm font-black font-mono-nums text-[#17301C]">{f.production_kwh}</div>
                              <div className="text-[9px] text-[#6E7A71]">kWh · {f.confidence_pct}%</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ANOMALIE */}
                  {anomalies && anomalies.length > 0 && (
                    <div className="card-surface p-6" data-testid="anomalies-panel">
                      <div className="label-eco mb-4 flex items-center gap-2"><AlertTriangle size={14} className="text-[#991B1B]" /> Notifiche anomalie ({anomalies.length})</div>
                      <div className="space-y-2">
                        {anomalies.map((a, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#FEF2F2] border border-[#FCA5A5]" data-testid={`anomaly-${i}`}>
                            <AlertTriangle size={16} className={a.severity === "high" ? "text-[#991B1B]" : "text-[#EAB308]"} />
                            <div className="flex-1 text-sm">
                              <div className="font-semibold text-[#17301C]">{a.message}</div>
                              <div className="text-xs text-[#6E7A71] font-mono-nums mt-1">{a.date} · {a.type} · severity: {a.severity}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI SUGGESTIONS */}
                  {suggestions && suggestions.length > 0 && (
                    <div className="card-surface p-6 bg-[#17301C] text-white border-[#17301C]" data-testid="ai-suggestions-panel">
                      <div className="text-xs uppercase tracking-widest font-bold text-[#EAB308] mb-4 flex items-center gap-2"><Sparkles size={14} /> Suggerimenti AI</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {suggestions.map((s, i) => (
                          <div key={i} className="p-4 rounded-lg bg-white/5 border border-white/10" data-testid={`suggestion-${i}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Lightbulb size={14} className="text-[#EAB308]" />
                              <div className="text-sm font-black text-[#EAB308]">{s.title}</div>
                            </div>
                            <div className="text-sm text-white/90">{s.body}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ENERGY PRICES */}
                  {energyPrices && (
                    <div className="card-surface p-6" data-testid="energy-prices-panel">
                      <div className="flex items-center justify-between mb-4">
                        <div className="label-eco">Prezzi energia PUN · 7 giorni</div>
                        <div className="text-xs text-[#6E7A71] font-mono-nums">€/kWh</div>
                      </div>
                      <ResponsiveContainer width="100%" height={140}>
                        <LineChart data={energyPrices.prices.map((p) => ({ date: p.date.slice(5), price: p.avg_price_eur_per_kwh }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#DDE0D8" />
                          <XAxis dataKey="date" stroke="#6E7A71" fontSize={11} />
                          <YAxis stroke="#6E7A71" fontSize={11} tickFormatter={(v) => `€${v.toFixed(3)}`} />
                          <Tooltip contentStyle={{ background: "#fff", border: "1px solid #DDE0D8", borderRadius: 8 }} />
                          <Line dataKey="price" stroke="#17301C" strokeWidth={2} dot={{ fill: "#EAB308", r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* SMART DEVICES */}
                  <DevicesPanel plantId={selected} devices={devices} onChange={async () => { const r = await api.get(`/plants/${selected}/devices`); setDevices(r.data); }} />
                </>
              )}

              {history.length > 0 && (
                <div className="card-surface p-6" data-testid="calc-history">
                  <div className="label-eco mb-4">{t.dashboard.history}</div>
                  <div className="space-y-2">
                    {history.map((h) => (
                      <div key={h.id} className="flex items-center justify-between py-3 border-b border-[#DDE0D8] last:border-0">
                        <div>
                          <div className="font-semibold text-[#17301C]">{h.label}</div>
                          <div className="text-xs text-[#6E7A71]">{new Date(h.created_at).toLocaleString()}</div>
                        </div>
                        <div className="flex items-center gap-6 font-mono-nums text-sm">
                          <div className="text-[#17301C]"><b>{h.recommended_kwp}</b> kWp</div>
                          <div className="text-[#8BA892]"><b>€{h.annual_savings_eur.toLocaleString()}</b>/y</div>
                          <button onClick={() => deleteCalc(h.id)} className="text-[#991B1B] hover:opacity-70"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const KpiCard = ({ icon: Icon, label, value, unit, accent }) => (
  <div className={`card-surface p-4 ${accent ? "bg-[#EAB308] border-[#EAB308]" : ""}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={14} className={accent ? "text-[#17301C]" : "text-[#6E7A71]"} />
      <div className={`text-xs uppercase tracking-widest font-bold ${accent ? "text-[#17301C]" : "text-[#6E7A71]"}`}>{label}</div>
    </div>
    <div className={`text-3xl font-black font-mono-nums ${accent ? "text-[#17301C]" : "text-[#17301C]"}`}>
      {value}<span className="text-xs ml-1 font-medium opacity-70">{unit}</span>
    </div>
  </div>
);

const LiveKpi = ({ icon: Icon, label, value, unit, hint, accent }) => (
  <div className={`p-3 rounded-lg border ${accent ? "bg-[#17301C] text-white border-[#17301C]" : "bg-white border-[#DDE0D8]"}`}>
    <div className="flex items-center gap-2">
      <Icon size={13} className={accent ? "text-[#EAB308]" : "text-[#6E7A71]"} />
      <div className={`text-[10px] uppercase tracking-widest font-bold ${accent ? "text-[#EAB308]" : "text-[#6E7A71]"}`}>{label}</div>
    </div>
    <div className={`text-2xl font-black font-mono-nums mt-1 ${accent ? "text-[#EAB308]" : "text-[#17301C]"}`}>
      {value}<span className="text-xs ml-1 font-medium opacity-80">{unit}</span>
    </div>
    {hint && <div className={`text-[10px] mt-1 ${accent ? "text-white/70" : "text-[#6E7A71]"}`}>{hint}</div>}
  </div>
);

const EmailPrefsToggle = () => {
  const [enabled, setEnabled] = useState(true);
  useEffect(() => { api.get("/notifications/prefs").then(({ data }) => setEnabled(data.email_alerts !== false)).catch(() => {}); }, []);
  const toggle = async () => {
    const next = !enabled;
    try {
      const { data } = await api.patch("/notifications/prefs", { email_alerts: next });
      setEnabled(next);
      if (!data.email_service_configured && next) {
        toast.warning("Servizio email non configurato: aggiungi RESEND_API_KEY in backend/.env");
      } else {
        toast.success(next ? "Alert email ATTIVI" : "Alert email disattivati");
      }
    } catch { toast.error("Errore"); }
  };
  return (
    <button onClick={toggle} data-testid="email-prefs-toggle"
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border text-xs font-bold transition-colors ${
        enabled ? "bg-white border-[#166534] text-[#166534]" : "bg-white border-[#DDE0D8] text-[#6E7A71]"
      }`}>
      {enabled ? <Mail size={13} /> : <BellOff size={13} />}
      Alert email {enabled ? "ON" : "OFF"}
    </button>
  );
};

const AnomalyCheckButton = ({ plantId }) => {
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/plants/${plantId}/check-anomaly`);
      if (data?.status === "anomaly") {
        if (data.email_sent) toast.warning(`Anomalia rilevata: -${data.drop_pct}%. Email inviata.`, { duration: 8000 });
        else toast.warning(`Anomalia rilevata: -${data.drop_pct}%. (${data.reason || "no email"})`, { duration: 8000 });
      } else if (data?.status === "ok") {
        toast.success(`Impianto OK — produzione nella norma (${data.drop_pct >= 0 ? "-" : "+"}${Math.abs(data.drop_pct)}%)`);
      } else {
        toast.info(`Check saltato: ${data?.reason || "dati insufficienti"} — collega un inverter SolarEdge per il monitoraggio live.`);
      }
    } catch (e) { toast.error("Errore check anomalia"); }
    finally { setLoading(false); }
  };
  return (
    <button onClick={run} disabled={loading} data-testid="anomaly-check-btn"
      className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[#DDE0D8] bg-white text-[#17301C] text-xs font-bold hover:border-[#EAB308] transition-colors disabled:opacity-50">
      <AlertTriangle size={13} className="text-[#EAB308]" />
      {loading ? "Controllo..." : "Verifica anomalie"}
    </button>
  );
};

const DEVICE_TYPES = [
  { key: "ac", label: "Aria condizionata", power_w: 1200 },
  { key: "washing", label: "Lavatrice", power_w: 2000 },
  { key: "fridge", label: "Frigorifero", power_w: 150 },
  { key: "ev_charger", label: "Colonnina EV", power_w: 7400 },
  { key: "pump", label: "Pompa di calore", power_w: 3000 },
  { key: "light", label: "Illuminazione", power_w: 200 },
  { key: "other", label: "Altro", power_w: 500 },
];

const DevicesPanel = ({ plantId, devices, onChange }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("ac");
  const [power, setPower] = useState(1200);

  const add = async () => {
    if (!name.trim()) return;
    await api.post(`/plants/${plantId}/devices`, { name, type, power_w: power, on: false });
    setName(""); setOpen(false);
    onChange();
  };
  const toggle = async (id) => { await api.patch(`/devices/${id}/toggle`); onChange(); };
  const del = async (id) => { await api.delete(`/devices/${id}`); onChange(); };

  return (
    <div className="card-surface p-6" data-testid="devices-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eco">Dispositivi smart · {devices.length}</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-dash" data-testid="add-device-btn"><Plus size={14} /> Aggiungi</button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader><DialogTitle className="text-[#17301C] font-black">Aggiungi dispositivo</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <input data-testid="device-name" placeholder="Nome (es. Condizionatore salotto)" value={name} onChange={(e) => setName(e.target.value)}
                className="inp" />
              <select data-testid="device-type" value={type} onChange={(e) => { setType(e.target.value); const preset = DEVICE_TYPES.find((d) => d.key === e.target.value); if (preset) setPower(preset.power_w); }} className="inp">
                {DEVICE_TYPES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
              <input data-testid="device-power" type="number" placeholder="Potenza W" value={power} onChange={(e) => setPower(parseInt(e.target.value))} className="inp" />
              <button data-testid="device-submit" onClick={add} className="btn-primary w-full">Aggiungi</button>
              <style>{`.inp{width:100%;padding:0.65rem 0.75rem;border-radius:0.5rem;border:1px solid #DDE0D8;background:white;font-size:0.925rem}.inp:focus{outline:none;border-color:#17301C}`}</style>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {devices.length === 0 ? (
        <p className="text-sm text-[#6E7A71]">Nessun dispositivo. Aggiungi il primo per gestirlo in remoto.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {devices.map((d) => {
            const DT = DEVICE_TYPES.find((x) => x.key === d.type);
            return (
              <div key={d.id} className="flex items-center gap-3 p-3 rounded-lg border border-[#DDE0D8] bg-white" data-testid={`device-${d.id}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${d.on ? "bg-[#EAB308]" : "bg-[#EAE7E0]"}`}>
                  <Zap size={16} className={d.on ? "text-[#17301C]" : "text-[#6E7A71]"} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#17301C] truncate">{d.name}</div>
                  <div className="text-xs text-[#6E7A71] font-mono-nums">{DT?.label || d.type} · {d.power_w}W</div>
                </div>
                <button onClick={() => toggle(d.id)} data-testid={`toggle-device-${d.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold ${d.on ? "bg-[#166534] text-white" : "bg-[#DDE0D8] text-[#6E7A71]"}`}>
                  {d.on ? "ON" : "OFF"}
                </button>
                <button onClick={() => del(d.id)} className="text-[#991B1B] p-1"><Trash2 size={13} /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ChartCard = ({ title, children }) => (
  <div className="card-surface p-5">
    <div className="label-eco mb-4">{title}</div>
    {children}
  </div>
);

const InsightsPanel = ({ data }) => {
  if (!data) return null;
  const { forecast_today, highlights = [], economic, comparison } = data;
  const dyd = comparison?.today_vs_yesterday || {};
  const mvm = comparison?.month_vs_last_month || {};

  const toneColors = {
    success: "bg-[#8BA892]/15 border-[#8BA892] text-[#166534]",
    warning: "bg-[#EAB308]/15 border-[#EAB308] text-[#8B7000]",
    info: "bg-[#17301C]/8 border-[#17301C]/30 text-[#17301C]",
    tip: "bg-[#C45B41]/10 border-[#C45B41]/40 text-[#C45B41]",
  };

  const iconMap = { "trending-up": TrendingUp, "cloud": Cloud, "zap": Zap, "moon": Sparkles, "battery": Battery, "wrench": Wrench, "sun": Sun };

  return (
    <div className="card-surface p-6 relative overflow-hidden" data-testid="insights-panel">
      <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#EAB308]/10 blur-3xl pointer-events-none" />

      {/* Forecast Today (hero) */}
      <div className="relative flex items-start justify-between gap-4 mb-6 pb-6 border-b border-[#DDE0D8]">
        <div className="flex-1 min-w-0">
          <div className="label-eco mb-2">
            <span className="inline-block w-2 h-2 rounded-full bg-[#EAB308] mr-2 align-middle animate-pulse" />
            Il tuo impianto oggi
          </div>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-5xl sm:text-6xl font-black font-mono-nums text-[#17301C]" data-testid="insights-forecast-kwh">
              {forecast_today.kwh}
            </span>
            <span className="text-2xl font-black text-[#EAB308]">kWh</span>
            <span className="text-sm text-[#6E7A71] ml-2 hidden sm:inline">produzione stimata</span>
          </div>
          <div className="text-sm text-[#3A4A3E] mt-1">
            💶 <span className="font-black text-[#17301C] font-mono-nums">€ {forecast_today.gain_eur.toFixed(2)}</span> di guadagno oggi
          </div>
        </div>
        <div className="text-right hidden md:block">
          <div className="text-[10px] font-black tracking-widest uppercase text-[#6E7A71]">Vs ieri</div>
          <div className={`text-2xl font-black font-mono-nums ${dyd.diff_pct >= 0 ? "text-[#166534]" : "text-[#C45B41]"}`}>
            {dyd.diff_pct >= 0 ? "+" : ""}{dyd.diff_pct}%
          </div>
          <div className="text-xs text-[#6E7A71] mt-1">{dyd.today_kwh} vs {dyd.yesterday_kwh} kWh</div>
        </div>
      </div>

      {/* AI Highlights (proactive messages) */}
      {highlights.length > 0 && (
        <div className="mb-6 space-y-2">
          {highlights.map((h, i) => {
            const I = iconMap[h.icon] || Lightbulb;
            return (
              <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border ${toneColors[h.tone] || toneColors.info}`} data-testid={`insight-${i}`}>
                <I size={18} className="flex-shrink-0 mt-0.5" />
                <div className="text-sm leading-relaxed">{h.text}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Economic Summary Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <EcoBox label="Guadagno oggi" value={`€${economic.today_gain_eur.toFixed(2)}`} sub={`${economic.today_kwh} kWh`} accent="#EAB308" testid="eco-today" />
        <EcoBox label="Guadagno mese" value={`€${economic.month_gain_eur.toFixed(0)}`} sub={`${economic.month_kwh} kWh`} testid="eco-month" />
        <EcoBox label="Guadagno anno" value={`€${economic.year_gain_eur.toFixed(0)}`} sub={`${economic.year_kwh.toLocaleString("it-IT")} kWh`} testid="eco-year" />
        <EcoBox label="Totale lifetime" value={`€${economic.lifetime_total_gain_eur.toLocaleString("it-IT")}`} sub={`in ${(economic.days_operational / 365).toFixed(1)} anni`} accent="#166534" testid="eco-lifetime" />
      </div>

      {/* CO2 + ROI + Comparison bar */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="p-3 rounded-xl bg-[#8BA892]/15 border border-[#8BA892]/40" data-testid="eco-co2">
          <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-[#166534]">
            <Leaf size={12} /> CO₂ evitata
          </div>
          <div className="text-xl font-black text-[#166534] font-mono-nums mt-1">
            {economic.co2_avoided_kg.toLocaleString("it-IT")} kg
          </div>
          <div className="text-[11px] text-[#3A4A3E] mt-0.5">= {economic.co2_avoided_trees} alberi 🌳</div>
        </div>

        <div className="p-3 rounded-xl bg-[#17301C] text-white" data-testid="eco-roi">
          <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-[#EAB308]">
            <TrendingUp size={12} /> Rientro investimento
          </div>
          <div className="text-xl font-black text-white font-mono-nums mt-1">
            {economic.payback_years} anni
          </div>
          <div className="text-[11px] text-white/70 mt-0.5">Tempo stimato al ROI</div>
        </div>

        <div className="p-3 rounded-xl bg-white border-2 border-[#DDE0D8]" data-testid="eco-vs-month">
          <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-[#6E7A71]">
            <LineChartIcon size={12} /> Mese vs precedente
          </div>
          <div className={`text-xl font-black font-mono-nums mt-1 ${mvm.diff_pct >= 0 ? "text-[#166534]" : "text-[#C45B41]"}`}>
            {mvm.diff_pct >= 0 ? "+" : ""}{mvm.diff_pct}%
          </div>
          <div className="text-[11px] text-[#6E7A71] mt-0.5">
            {mvm.current_kwh?.toLocaleString("it-IT")} vs {mvm.previous_kwh?.toLocaleString("it-IT")} kWh
          </div>
        </div>
      </div>
    </div>
  );
};

const EcoBox = ({ label, value, sub, accent, testid }) => (
  <div className="p-3 rounded-xl bg-[#EAE7E0]" data-testid={testid}>
    <div className="text-[10px] font-black uppercase tracking-widest text-[#6E7A71]">{label}</div>
    <div className="text-xl font-black font-mono-nums mt-1" style={{ color: accent || "#17301C" }}>{value}</div>
    <div className="text-[11px] text-[#6E7A71] mt-0.5">{sub}</div>
  </div>
);

const PlantTabBtn = ({ v, label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`plant-tab-${v}`}
    className={`px-3 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${active ? "bg-[#17301C] text-white" : "bg-[#EAE7E0] text-[#6E7A71] hover:bg-[#DDE0D8]"}`}
  >{label}</button>
);

const AddPlantDialog = ({ open, setOpen, onCreated }) => {
  const [form, setForm] = useState({
    name: "", kwp: 6, location: "Roma", installed_date: new Date().toISOString().slice(0, 10),
    panels: 14, battery_kwh: 0, inverter_brand: "SolarEdge",
    inverter_model: "", panel_brand: "", panel_model: "",
    orientation: "south", tilt_degrees: 30, shading: "none",
    address: "", installer: "", warranty_years: 25,
    energy_provider: "", energy_price_eur_kwh: 0.28,
    feed_in_tariff_eur_kwh: 0.10, monthly_kwh_consumption: 0,
  });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("basic"); // basic | tech | contract

  const upd = (k, cast = (v) => v) => (e) => setForm({ ...form, [k]: cast(e.target.value) });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/plants", form);
      toast.success("Impianto aggiunto");
      setOpen(false);
      onCreated();
    } catch { toast.error("Errore salvataggio"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-primary" data-testid="add-plant-btn"><Plus size={16} /> Aggiungi impianto</button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#17301C] font-black">Nuovo impianto — dati dettagliati</DialogTitle>
          <DialogDescription className="text-sm text-[#6E7A71]">
            Compila i 3 gruppi (Base, Tecnici, Contratto) per un monitoraggio preciso.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 mt-2 mb-4">
          <PlantTabBtn v="basic" label="Base" active={tab === "basic"} onClick={() => setTab("basic")} />
          <PlantTabBtn v="tech" label="Tecnici" active={tab === "tech"} onClick={() => setTab("tech")} />
          <PlantTabBtn v="contract" label="Contratto" active={tab === "contract"} onClick={() => setTab("contract")} />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {tab === "basic" && (
            <>
              <input required data-testid="plant-name" placeholder="Nome impianto (es. Casa Roma)" value={form.name} onChange={upd("name")} className="inp" />
              <div className="grid grid-cols-2 gap-3">
                <input required data-testid="plant-kwp" type="number" step="0.1" placeholder="kWp totale" value={form.kwp} onChange={upd("kwp", parseFloat)} className="inp" />
                <input required data-testid="plant-panels" type="number" placeholder="N° pannelli" value={form.panels} onChange={upd("panels", parseInt)} className="inp" />
              </div>
              <input required data-testid="plant-location" placeholder="Città (es. Milano)" value={form.location} onChange={upd("location")} className="inp" />
              <input data-testid="plant-address" placeholder="Indirizzo completo (opzionale)" value={form.address} onChange={upd("address")} className="inp" />
              <div className="grid grid-cols-2 gap-3">
                <input required data-testid="plant-date" type="date" value={form.installed_date} onChange={upd("installed_date")} className="inp" />
                <input data-testid="plant-battery" type="number" step="0.1" placeholder="Batteria kWh (0 se assente)" value={form.battery_kwh} onChange={upd("battery_kwh", parseFloat)} className="inp" />
              </div>
            </>
          )}

          {tab === "tech" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="plant-inverter-brand" value={form.inverter_brand} onChange={upd("inverter_brand")} className="inp">
                  <option>SolarEdge</option><option>Huawei</option><option>Fronius</option>
                  <option>Growatt</option><option>Enphase</option><option>Solis</option>
                  <option>GoodWe</option><option>SMA</option><option>ZCS Azzurro</option><option>Altro</option>
                </select>
                <input data-testid="plant-inverter-model" placeholder="Modello inverter (es. SE7K)" value={form.inverter_model} onChange={upd("inverter_model")} className="inp" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="plant-panel-brand" placeholder="Marca pannelli (es. LG)" value={form.panel_brand} onChange={upd("panel_brand")} className="inp" />
                <input data-testid="plant-panel-model" placeholder="Modello (es. NeON 380W)" value={form.panel_model} onChange={upd("panel_model")} className="inp" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <select data-testid="plant-orientation" value={form.orientation} onChange={upd("orientation")} className="inp">
                  <option value="south">Sud</option>
                  <option value="se">Sud-Est</option>
                  <option value="sw">Sud-Ovest</option>
                  <option value="east">Est</option>
                  <option value="west">Ovest</option>
                  <option value="north">Nord</option>
                </select>
                <input data-testid="plant-tilt" type="number" placeholder="Inclinazione °" value={form.tilt_degrees} onChange={upd("tilt_degrees", parseInt)} className="inp" />
                <select data-testid="plant-shading" value={form.shading} onChange={upd("shading")} className="inp">
                  <option value="none">Nessuna ombra</option>
                  <option value="partial">Ombra parziale</option>
                  <option value="significant">Ombra significativa</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="plant-installer" placeholder="Installatore" value={form.installer} onChange={upd("installer")} className="inp" />
                <input data-testid="plant-warranty" type="number" placeholder="Anni garanzia" value={form.warranty_years} onChange={upd("warranty_years", parseInt)} className="inp" />
              </div>
            </>
          )}

          {tab === "contract" && (
            <>
              <input data-testid="plant-energy-provider" placeholder="Gestore energia (es. Enel, A2A, Iren)" value={form.energy_provider} onChange={upd("energy_provider")} className="inp" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#6E7A71] font-bold mb-1 block">Prezzo €/kWh (acquisto)</label>
                  <input data-testid="plant-price" type="number" step="0.01" value={form.energy_price_eur_kwh} onChange={upd("energy_price_eur_kwh", parseFloat)} className="inp" />
                </div>
                <div>
                  <label className="text-xs text-[#6E7A71] font-bold mb-1 block">Tariffa vendita €/kWh</label>
                  <input data-testid="plant-tariff" type="number" step="0.01" value={form.feed_in_tariff_eur_kwh} onChange={upd("feed_in_tariff_eur_kwh", parseFloat)} className="inp" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#6E7A71] font-bold mb-1 block">Consumo mensile stimato (kWh)</label>
                <input data-testid="plant-consumption" type="number" placeholder="Es. 350" value={form.monthly_kwh_consumption} onChange={upd("monthly_kwh_consumption", parseFloat)} className="inp" />
              </div>
              <div className="p-3 rounded-lg bg-[#EAB308]/10 border border-[#EAB308]/30 text-xs text-[#17301C]">
                💡 <b>Suggerimento</b>: dopo aver salvato l&apos;impianto potrai collegare l&apos;inverter reale via API per il monitoraggio live (SolarEdge, Growatt, Huawei, Fronius).
              </div>
            </>
          )}

          <DialogFooter className="pt-2">
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="plant-submit">
              {loading ? "..." : "Salva impianto"}
            </button>
          </DialogFooter>
        </form>
        <style>{`.inp { width:100%; padding: 0.6rem 0.75rem; border-radius: 0.5rem; border: 1px solid #DDE0D8; background: white; font-size: 0.925rem; } .inp:focus { outline: none; border-color: #17301C; box-shadow: 0 0 0 1px #17301C; }`}</style>
      </DialogContent>
    </Dialog>
  );
};

const ConnectInverterDialog = ({ plantId, plantName, open, setOpen }) => {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("solaredge");
  const [credentials, setCredentials] = useState({});
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [existing, setExisting] = useState(null);

  React.useEffect(() => {
    if (!open) return;
    api.get("/inverter-brands").then(({ data }) => setBrands(data.brands || []));
    api.get(`/plants/${plantId}/connect`).then(({ data }) => setExisting(data.connected ? data : null)).catch(() => {});
  }, [open, plantId]);

  React.useEffect(() => {
    setCredentials({});
    setTestResult(null);
  }, [selectedBrand]);

  const currentBrand = brands.find((b) => b.key === selectedBrand);

  const testConn = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/plants/${plantId}/connect/test`, {
        brand: selectedBrand,
        credentials,
      });
      setTestResult(data);
      if (data.ok) toast.success(data.message || "Test riuscito");
      else toast.error(data.error || "Test fallito");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore test");
    } finally { setTesting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/plants/${plantId}/connect`, {
        brand: selectedBrand,
        credentials,
      });
      toast.success(data.message || "Inverter collegato!");
      setExisting({ connected: true, brand: selectedBrand, brand_label: data.brand_label, status: data.status, coming_soon: !data.supported });
      setTestResult(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!confirm("Scollegare l'inverter da questo impianto?")) return;
    try {
      await api.delete(`/plants/${plantId}/connect`);
      toast.success("Inverter scollegato");
      setExisting(null);
    } catch { toast.error("Errore"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#17301C] font-black">
            Collega inverter — {plantName}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6E7A71]">
            Inserisci le credenziali del cloud del tuo inverter per il monitoraggio in tempo reale.
          </DialogDescription>
        </DialogHeader>

        {existing ? (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-[#8BA892]/15 border border-[#8BA892]" data-testid="inverter-connected">
              <div className="flex items-center gap-2 text-[#17301C] font-black">
                <Zap size={16} className="text-[#EAB308]" />
                {existing.brand_label} — {existing.coming_soon ? "In attivazione" : "Connesso ✓"}
              </div>
              <div className="text-xs text-[#3A4A3E] mt-1">
                {existing.coming_soon
                  ? "Le credenziali sono state salvate. Il monitoraggio real-time sarà attivato entro 48h."
                  : `Ultimo aggiornamento: ${existing.last_sync ? new Date(existing.last_sync).toLocaleString("it-IT") : "in corso..."}`}
              </div>
            </div>
            <button onClick={disconnect} data-testid="inverter-disconnect" className="w-full px-4 py-2 rounded-lg border-2 border-[#991B1B] text-[#991B1B] font-bold hover:bg-[#991B1B] hover:text-white transition-colors text-sm">
              Scollega inverter
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-black uppercase tracking-widest text-[#6E7A71] mb-2 block">Marca inverter</label>
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                data-testid="inverter-brand-select"
                className="w-full px-3 py-2.5 rounded-lg border-2 border-[#DDE0D8] bg-white text-[#17301C] font-semibold"
              >
                {brands.map((b) => (
                  <option key={b.key} value={b.key}>
                    {b.label} {b.supported ? "✓" : "(attivazione entro 48h)"}
                  </option>
                ))}
              </select>
            </div>

            {currentBrand?.fields?.map((f) => (
              <div key={f.key}>
                <label className="text-xs font-bold text-[#17301C] mb-1 block">{f.label} *</label>
                <input
                  type={f.type}
                  value={credentials[f.key] || ""}
                  onChange={(e) => setCredentials({ ...credentials, [f.key]: e.target.value })}
                  placeholder={f.label}
                  data-testid={`inverter-cred-${f.key}`}
                  className="w-full px-3 py-2.5 rounded-lg border-2 border-[#DDE0D8] bg-white text-[#17301C]"
                />
                {f.help && <div className="text-[11px] text-[#6E7A71] mt-1">{f.help}</div>}
              </div>
            ))}

            {testResult && (
              <div className={`p-3 rounded-lg text-sm ${testResult.ok ? "bg-[#8BA892]/15 border border-[#8BA892] text-[#17301C]" : "bg-[#991B1B]/10 border border-[#991B1B] text-[#991B1B]"}`}>
                {testResult.ok ? (
                  <>
                    ✅ Test riuscito
                    {testResult.site_name && <div className="text-xs mt-1">Impianto: <b>{testResult.site_name}</b></div>}
                    {testResult.peak_power_kwp > 0 && <div className="text-xs">Potenza: {testResult.peak_power_kwp} kWp</div>}
                    {testResult.coming_soon && <div className="text-xs mt-1">{testResult.message}</div>}
                  </>
                ) : (
                  <>❌ {testResult.error}</>
                )}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button onClick={testConn} disabled={testing || saving} data-testid="inverter-test-btn" className="flex-1 px-4 py-2.5 rounded-lg border-2 border-[#17301C] text-[#17301C] font-bold hover:bg-[#17301C] hover:text-white transition-colors disabled:opacity-50">
                {testing ? "Test..." : "Testa"}
              </button>
              <button onClick={save} disabled={testing || saving || !currentBrand?.required?.every((k) => (credentials[k] || "").trim())} data-testid="inverter-save-btn" className="flex-1 btn-primary">
                {saving ? "..." : "Collega"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
